import React, { useState, useEffect, useCallback } from 'react';
import { employeeV2Service } from '../../../services/api';
import {
    C, fmtDate,
    cardStyle, cardHeaderStyle, cardTitleStyle, labelStyle, valueStyle, inputStyle,
    primaryButtonStyle, secondaryButtonStyle, linkButtonStyle,
} from './shared';

const DOC_TYPES = ['passport', 'visa', 'contract', 'id_card', 'certificate', 'other'];
const DOC_TYPE_KEYS = {
    passport: 'doc_type_passport',
    visa: 'doc_type_visa',
    contract: 'doc_type_contract',
    id_card: 'doc_type_id_card',
    certificate: 'doc_type_certificate',
    other: 'doc_type_other',
};

// ─── Field helpers ────────────────────────────────────────────────────────────

const FieldRow = ({ label, children }) => (
    <div>
        <label style={labelStyle}>{label}</label>
        <div style={valueStyle}>{children}</div>
    </div>
);

// ─── Contact card ─────────────────────────────────────────────────────────────

const LANGUAGES = [{ value: 'en', labelKey: 'english' }, { value: 'ar', labelKey: 'arabic' }];

const ContactCard = ({ employee, t }) => (
    <div style={cardStyle}>
        <div style={cardHeaderStyle}>
            <h3 style={cardTitleStyle}>{t('contact_info')}</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
            <FieldRow label={t('phone')}>{employee.personal?.phone || '—'}</FieldRow>
            <FieldRow label={t('email')}>{employee.personal?.email || '—'}</FieldRow>
            <FieldRow label={t('preferred_language')}>
                {employee.personal?.preferred_language
                    ? t(LANGUAGES.find(l => l.value === employee.personal.preferred_language)?.labelKey || 'english')
                    : '—'}
            </FieldRow>
        </div>
    </div>
);

// ─── Personal card ────────────────────────────────────────────────────────────

const PersonalCard = ({ employee, t }) => {
    const p = employee.personal || {};
    return (
        <div style={cardStyle}>
            <div style={cardHeaderStyle}>
                <h3 style={cardTitleStyle}>{t('personal_info')}</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                <FieldRow label={t('date_of_birth')}>{p.date_of_birth ? fmtDate(p.date_of_birth) : '—'}</FieldRow>
                <FieldRow label={t('address')}>{p.address || '—'}</FieldRow>
                <FieldRow label={t('emergency_contact')}>
                    {p.emergency_contact_name ? (
                        <>
                            {p.emergency_contact_name}
                            {p.emergency_contact_relationship ? ` · ${p.emergency_contact_relationship}` : ''}
                            {p.emergency_contact_phone ? <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{p.emergency_contact_phone}</div> : null}
                        </>
                    ) : '—'}
                </FieldRow>
            </div>
        </div>
    );
};

// ─── Documents card ───────────────────────────────────────────────────────────

const UploadModal = ({ employeeId, onClose, onUploaded, t }) => {
    const [docType, setDocType] = useState('passport');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        if (!file) return setError(t('select_file_first'));
        try {
            setUploading(true);
            setError('');
            await employeeV2Service.uploadDocument(employeeId, file, docType);
            await onUploaded();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.detail || t('failed_to_upload'));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 440, padding: '24px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('upload_document')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '8px 12px', fontSize: 12, color: C.redText, marginBottom: 12 }}>{error}</div>}
                <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>{t('document_type')}</label>
                    <select style={inputStyle} value={docType} onChange={(e) => setDocType(e.target.value)}>
                        {DOC_TYPES.map(dt => <option key={dt} value={dt}>{t(DOC_TYPE_KEYS[dt])}</option>)}
                    </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>{t('file')}</label>
                    <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
                        style={{ ...inputStyle, padding: '6px 8px' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={secondaryButtonStyle}>{t('cancel')}</button>
                    <button onClick={submit} disabled={uploading}
                        style={{ ...primaryButtonStyle, opacity: uploading ? 0.7 : 1, cursor: uploading ? 'default' : 'pointer' }}>
                        {uploading ? t('uploading') : t('upload')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const DocumentsCard = ({ employee, t }) => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await employeeV2Service.listDocuments(employee.employee_id);
            setDocs(data);
        } catch {
            setError(t('failed_to_load_documents'));
        } finally {
            setLoading(false);
        }
    }, [employee.employee_id, t]);

    useEffect(() => { load(); }, [load]);

    const download = async (doc) => {
        try {
            const res = await employeeV2Service.getDocumentSignedUrl(employee.employee_id, doc.document_id);
            if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
        } catch {
            setError(t('failed_to_load_documents'));
        }
    };

    const remove = async (doc) => {
        // eslint-disable-next-line no-restricted-globals, no-alert
        if (!window.confirm(`${t('confirm_delete_document')} ${doc.filename}?`)) return;
        try {
            await employeeV2Service.deleteDocument(employee.employee_id, doc.document_id);
            await load();
        } catch {
            setError(t('failed_to_load_documents'));
        }
    };

    return (
        <div style={cardStyle}>
            <div style={cardHeaderStyle}>
                <h3 style={cardTitleStyle}>{t('documents')}</h3>
                <button onClick={() => setShowUpload(true)} style={linkButtonStyle}>{t('upload_document')}</button>
            </div>

            {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '8px 12px', fontSize: 12, color: C.redText, marginBottom: 12 }}>{error}</div>}

            {loading ? (
                <div style={{ fontSize: 13, color: C.mutedLight, padding: '16px 0' }}>{t('loading')}</div>
            ) : docs.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '24px 0', color: C.mutedLight }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span style={{ fontSize: 13 }}>{t('no_documents_uploaded')}</span>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {docs.map(d => (
                        <div key={d.document_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8 }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</div>
                                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                    {t(DOC_TYPE_KEYS[d.document_type] || 'doc_type_other')} · {fmtDate(d.uploaded_at)}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                <button onClick={() => download(d)} style={{ background: 'none', border: 'none', color: C.green, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '4px 8px', borderRadius: 6 }}>{t('download')}</button>
                                <button onClick={() => remove(d)} style={{ background: 'none', border: 'none', color: C.redText, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '4px 8px', borderRadius: 6 }}>{t('delete')}</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showUpload && (
                <UploadModal employeeId={employee.employee_id} onClose={() => setShowUpload(false)} onUploaded={load} t={t} />
            )}
        </div>
    );
};

// ─── Main export ──────────────────────────────────────────────────────────────

const TabPersonalInfo = ({ employee, onRefresh, t }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ContactCard employee={employee} t={t} />
        <PersonalCard employee={employee} t={t} />
        <DocumentsCard employee={employee} t={t} />
    </div>
);

export default TabPersonalInfo;
